/**
 * First-party Execura collect for doimpo.com.
 * Posts same-origin; the API forwards to Execura /api/site-analytics/collect.
 * Runs alongside Vercel Analytics (and optional GTM).
 */
const SESSION_KEY = "execura_sa_session";
const LAST_PATH_KEY = "execura_sa_last_path";
const MAX_BUFFER = 40;
const FLUSH_MS = 4000;
const DOIMPO_SITE_PROJECT_ID = "c0ee0b33-4149-4345-b225-bb2666fa4040";
const DEFAULT_SITE_ID = "doimpo.com";
const COLLECT_PATH = "/api/site-analytics/collect";

type EventName = "page_view" | "journey_step" | "error" | "web_vital" | "session_start";

type AnalyticsEvent = {
  name: EventName;
  ts: number;
  path?: string;
  title?: string;
  referrer?: string;
  props?: Record<string, unknown>;
};

let sessionId: string | null = null;
const buffer: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersInstalled = false;
let lastPath: string | null = null;
let sessionStarted = false;
let started = false;

function readLastPath(): string | null {
  try {
    return sessionStorage.getItem(LAST_PATH_KEY);
  } catch {
    return null;
  }
}

function setLastPath(path: string) {
  lastPath = path;
  try {
    sessionStorage.setItem(LAST_PATH_KEY, path);
  } catch {
    // ignore
  }
}

function currentPath(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname + window.location.search;
}

function shouldSkipPath(path: string): boolean {
  return path.startsWith("/admin");
}

function ensureSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      sessionId = existing;
      return existing;
    }
  } catch {
    // ignore
  }
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  sessionId = id;
  try {
    sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    // ignore
  }
  return id;
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushExecuraSiteAnalytics();
  }, FLUSH_MS);
}

function enqueue(event: AnalyticsEvent) {
  if (shouldSkipPath(event.path || currentPath())) return;
  if (buffer.length >= MAX_BUFFER) buffer.shift();
  buffer.push(event);
  scheduleFlush();
}

export function trackExecuraSiteEvent(
  name: EventName,
  props?: Record<string, unknown>,
  path?: string,
) {
  try {
    enqueue({
      name,
      ts: Date.now(),
      path: path ?? currentPath(),
      title: typeof document !== "undefined" ? document.title : undefined,
      referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
      props,
    });
  } catch {
    // never break the site
  }
}

export function trackExecuraPageView(path?: string) {
  const p = path ?? currentPath();
  if (shouldSkipPath(p)) return;
  if (!lastPath) lastPath = readLastPath();
  if (lastPath === p) return;
  if (!sessionStarted) {
    sessionStarted = true;
    trackExecuraSiteEvent("session_start", undefined, p);
  }
  if (lastPath && lastPath !== p) {
    trackExecuraSiteEvent("journey_step", { from: lastPath, to: p }, p);
  }
  setLastPath(p);
  trackExecuraSiteEvent("page_view", undefined, p);
}

export function captureExecuraSiteError(err: unknown, context?: Record<string, unknown>) {
  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "Unknown error";
  const stack = err instanceof Error ? err.stack?.slice(0, 2000) : undefined;
  trackExecuraSiteEvent("error", {
    message: message.slice(0, 500),
    stack,
    ...context,
  });
}

async function flushExecuraSiteAnalytics(): Promise<void> {
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, buffer.length);
  try {
    const body = JSON.stringify({
      siteId: DEFAULT_SITE_ID,
      projectId: DOIMPO_SITE_PROJECT_ID,
      sessionId: ensureSessionId(),
      events: batch,
    });
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(COLLECT_PATH, blob)) return;
    }
    await fetch(COLLECT_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    });
  } catch {
    // drop on failure
  }
}

function observeWebVitals() {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;
  try {
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const name = entry.name || entry.entryType;
        const value =
          "value" in entry && typeof (entry as PerformanceEntry & { value?: number }).value === "number"
            ? (entry as PerformanceEntry & { value: number }).value
            : entry.duration;
        if (name === "largest-contentful-paint" || entry.entryType === "largest-contentful-paint") {
          trackExecuraSiteEvent("web_vital", { metric: "LCP", value: Math.round(value) });
        }
        if (
          entry.entryType === "layout-shift" &&
          !(entry as PerformanceEntry & { hadRecentInput?: boolean }).hadRecentInput
        ) {
          trackExecuraSiteEvent("web_vital", {
            metric: "CLS",
            value: Number((value as number).toFixed?.(4) ?? value),
          });
        }
      }
    });
    po.observe({ type: "largest-contentful-paint", buffered: true } as PerformanceObserverInit);
    po.observe({ type: "layout-shift", buffered: true } as PerformanceObserverInit);
  } catch {
    // unsupported
  }
}

function installGlobalErrorListeners() {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;
  window.addEventListener("error", (ev) => {
    captureExecuraSiteError(ev.error ?? ev.message, { source: "window.error" });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    captureExecuraSiteError(ev.reason, { source: "unhandledrejection" });
  });
  window.addEventListener("pagehide", () => {
    void flushExecuraSiteAnalytics();
  });
}

export function initExecuraSiteAnalytics(): void {
  try {
    if (typeof window === "undefined" || started) return;
    started = true;
    ensureSessionId();
    installGlobalErrorListeners();
    observeWebVitals();
  } catch {
    // never block app
  }
}
